import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  Avatar,
  Button,
  IconButton,
  Typography,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import { IMAGE_BASE_URL } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { RemoveCircle } from "@mui/icons-material";
import errorHandler from "../../utils/errorHandler";
import { CategorySchema } from "../../utils/Schema";
import { apiCategory } from "../../services/productService";
import { trimFormValues } from "../../utils/helpers";

// Styled components for image preview container and remove button
const PreviewContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 100,
  height: 100,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: theme.shadows[3],
  cursor: "pointer",
  "&:hover .remove-btn": {
    opacity: 1,
  },
}));

const RemoveButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 4,
  right: 4,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  padding: 4,
  opacity: 0.8,
  transition: "opacity 0.3s",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
    opacity: 1,
  },
}));

function AddEditCategory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  // Single existing uploaded image (filename/URL)
  const [existingImage, setExistingImage] = useState(null);
  // New selected image as file
  const [selectedImage, setSelectedImage] = useState(null);
  // Preview URL for display
  const [imagePreview, setImagePreview] = useState(null);

  const fileInputRef = useRef(null);
  const { id } = useParams();

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await apiCategory.getById({ categoryId: id });
        const resData = response?.data?.data || {};
        const existingImg = resData?.image || resData?.categoryImage || null;
        // Handle both array and single value (take first if array)
        const imageUrl = Array.isArray(existingImg)
          ? existingImg[0]
          : existingImg;
        // Set initial image array for formik
        const initialImageArray = imageUrl ? [imageUrl] : [];

        setExistingImage(imageUrl);
        setSelectedImage(null);
        setImagePreview(imageUrl ? IMAGE_BASE_URL + imageUrl : null);
        setData(resData);
        // enableReinitialize will handle updating formik values
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  // Calculate initial image array
  const getInitialImageArray = () => {
    if (id && data) {
      const existingImg = data?.image || data?.categoryImage || null;
      const imageUrl = Array.isArray(existingImg)
        ? existingImg[0]
        : existingImg;
      return imageUrl ? [imageUrl] : [];
    }
    return [];
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: CategorySchema,
    initialValues: {
      name: data?.title || data?.name || "",
      description: data?.description || "",
      image: getInitialImageArray(),
      status: data?.status || 1,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Trim all string values in the form values
        const trimmedValues = trimFormValues(values);

        const formData = new FormData();
        formData.append("categoryId", id || "");
        formData.append("title", trimmedValues.name);
        formData.append("description", trimmedValues.description);

        // Append image as array - if new file selected, use that; otherwise use existing or empty string
        if (selectedImage) {
          formData.append("image", selectedImage);
        } else if (existingImage) {
          formData.append("image", existingImage);
        } else {
          formData.append("image", "");
        }

        const response = id
          ? await apiCategory.edit(formData)
          : await apiCategory.add(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleImageChange = (event) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    // Allowed file types
    const allowedTypes = ["image/png", "image/jpg", "image/jpeg", "image/webp"];
    const allowedExtensions = [".png", ".jpg", ".jpeg", ".webp"];
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    // Validate file type
    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      toast.error(
        `Invalid file format. Only PNG, JPG, JPEG, and WEBP formats are allowed.`,
      );
      event.target.value = "";
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
    formik.setFieldValue("image", [file]); // Set as array

    event.target.value = null;
  };

  const handleRemoveImage = () => {
    if (selectedImage) {
      // Remove newly selected image
      setSelectedImage(null);
      setImagePreview(existingImage ? IMAGE_BASE_URL + existingImage : null);
      formik.setFieldValue("image", existingImage ? [existingImage] : []);
    } else if (existingImage) {
      // Remove existing image
      setExistingImage(null);
      setImagePreview(null);
      formik.setFieldValue("image", []);
    }
  };

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Category</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Category`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                {/* Image Upload Section */}
                <Grid size={{ xs: 12 }} md={12} sx={{ mt: 2 }}>
                  <input
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                  <Box
                    mb={1}
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                    alignItems="center"
                  >
                    {!imagePreview && (
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ width: "100%" }}
                      >
                        No image uploaded yet
                      </Typography>
                    )}
                    {imagePreview && (
                      <PreviewContainer
                        title="Click to change image"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <Avatar
                          src={imagePreview}
                          alt="preview"
                          variant="rounded"
                          sx={{ width: "100%", height: "100%" }}
                        />
                        <RemoveButton
                          size="small"
                          aria-label="Remove image"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="remove-btn"
                        >
                          <RemoveCircle fontSize="small" />
                        </RemoveButton>
                      </PreviewContainer>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => fileInputRef.current.click()}
                    sx={{ textTransform: "none" }}
                  >
                    {id ? "Edit Image" : "Upload Image"}
                  </Button>
                  {formik.touched.image && formik.errors.image && (
                    <FormHelperText error>{formik.errors.image}</FormHelperText>
                  )}
                </Grid>

                {/* Category Name */}
                <Grid size={{ xs: 12 }} md={12} sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Main Category Name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    required
                    name="name"
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    inputProps={{ maxLength: 50 }}
                    placeholder="e.g., SUVs, Sedans, Trucks, Motorcycles"
                  />
                </Grid>

                {/* Description */}
                <Grid size={{ xs: 12 }} md={12} sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    name="description"
                    error={Boolean(
                      formik.touched.description && formik.errors.description,
                    )}
                    helperText={
                      formik.touched.description && formik.errors.description
                    }
                    inputProps={{ maxLength: 200 }}
                    placeholder="Brief description of the category for SEO and clarity"
                  />
                </Grid>

                {/* Submit Button */}
                <Grid size={{ xs: 12 }}>
                  <Box mt={3} width="100%">
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Category
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditCategory;
